import type { RaffleEntry, RoundResult } from '../types';

const RAFFLE_KEY = 'connect-360:raffle:v1';

const COMMUNITY_CODES: Record<string, string> = {
  henderson: 'HEN',
  oxford: 'OXF',
  louisburg: 'LOU',
  enfield: 'ENF',
  'warren county': 'WAR',
  'another community': 'COM',
};

export function qualifiesForRaffle(result: RoundResult): boolean {
  const communityMode = result.mode !== 'packet' && result.mode !== 'bonus';
  return communityMode && result.completed && result.totalConnections === 5 && result.correctConnections === 5;
}

export function communityAbbreviation(community: string): string {
  const normalized = community.trim().toLowerCase();
  return COMMUNITY_CODES[normalized]
    ?? normalized.replace(/[^a-z0-9]/g, '').slice(0, 3).toUpperCase().padEnd(3, 'X');
}

export function generateClaimCode(community: string, random: () => number = Math.random): string {
  const digits = 1000 + Math.floor(Math.min(0.999999, Math.max(0, random())) * 9000);
  return `${communityAbbreviation(community)}-${digits}`;
}

export function loadRaffleEntries(): RaffleEntry[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(RAFFLE_KEY) ?? '[]') as RaffleEntry[];
    return Array.isArray(parsed) ? parsed.filter((entry) => typeof entry.claimCode === 'string') : [];
  } catch {
    return [];
  }
}

export function saveRaffleEntries(entries: RaffleEntry[]): void {
  localStorage.setItem(RAFFLE_KEY, JSON.stringify(entries));
}

export function createRaffleEntry(result: RoundResult, initials: string, now = new Date()): RaffleEntry {
  if (!qualifiesForRaffle(result)) throw new Error('This round does not qualify for the raffle.');
  const entries = loadRaffleEntries();
  let claimCode = generateClaimCode(result.community ?? 'Another Community');
  for (let attempt = 0; entries.some((entry) => entry.claimCode === claimCode) && attempt < 20; attempt += 1) {
    claimCode = generateClaimCode(result.community ?? 'Another Community');
  }
  return {
    claimCode,
    initials,
    community: result.community ?? 'Another Community',
    score: result.score,
    completionTime: result.elapsedSeconds,
    timestamp: now.toISOString(),
    claimed: false,
  };
}

export function addRaffleEntry(entry: RaffleEntry): RaffleEntry[] {
  const entries = [entry, ...loadRaffleEntries()];
  saveRaffleEntries(entries);
  return entries;
}

export function setRaffleClaimed(claimCode: string, claimed: boolean): RaffleEntry[] {
  const entries = loadRaffleEntries().map((entry) => entry.claimCode === claimCode ? { ...entry, claimed } : entry);
  saveRaffleEntries(entries);
  return entries;
}

export function deleteRaffleEntry(claimCode: string): RaffleEntry[] {
  const entries = loadRaffleEntries().filter((entry) => entry.claimCode !== claimCode);
  saveRaffleEntries(entries);
  return entries;
}

export function clearRaffleEntries(): void {
  localStorage.removeItem(RAFFLE_KEY);
}

function csvCell(value: string | number | boolean): string {
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function raffleEntriesToCsv(entries: RaffleEntry[]): string {
  const header = ['claim_code', 'initials', 'community', 'score', 'completion_time', 'timestamp', 'claimed'];
  const rows = entries.map((entry) => [
    entry.claimCode, entry.initials, entry.community, entry.score, entry.completionTime, entry.timestamp, entry.claimed,
  ].map(csvCell).join(','));
  return [header.join(','), ...rows].join('\r\n');
}
